/**
 * 管理后台「用户与收入」三页的接口与类型：用户与计费、充值订单、激活码。
 *
 * 背景：这三页对应 Alice 的 /users*、/recharge-orders*、/activation-codes*、/activation-blocks*。
 *   本仓三个服务各管一部分：封禁、内部标记、地域豁免、封禁记录在账号服务（/admin/identity/accounts/{id}/...），
 *   用户列表、余额、Key、统计、流水、调账、激活码在控制面（/control/admin/...），
 *   合并订单列表与退款状态在支付服务（/billing/admin/recharge-orders*）。
 * 设计意图：
 *   - 不改共用的 admin.ts：这里自己发请求（admin.ts 没有导出 http 实例），遇到 401 先调 adminApi.me() 触发
 *     共用 http 层的会话刷新（同一时刻只刷新一次），成功后重试一次；失败时 me() 会通知应用回登录页。
 *   - 路径逐字对应后端；金额一律是整数 unit（1e-8 美元，1 花瓣 = 100,000 unit），页面里再换算。
 *   - 调账的 ref 由调用方在打开弹窗时生成一次，重试同一次提交时沿用，后端据此幂等。
 * 关键约束：错误统一抛 ApiError（status、code、message），页面按 code 映射文案；写操作的权限由后端判断。
 */
import { adminApi } from './admin.ts'
import { ApiError, type Query } from './http.ts'
import type { ApiKey } from './types.ts'

// ------------------------------------------------------------------ 用户

export type UserSort = 'balance' | 'cost' | 'requests' | 'tokens' | 'keys' | 'paid'

/** 账号类型：user 有登录入口，service 是服务账号（如 Yeou 游戏服务端），只由运营管理。 */
export type UserKind = 'user' | 'service'

export interface UserRow {
  account_id: string
  kind: UserKind
  email: string
  display_name: string
  status: string
  is_banned: boolean
  is_internal: boolean
  geo_exempt: boolean
  created_at: string
  paid_units: number
  bonus_units: number
  balance_units: number
  key_count: number
  total_cost_units: number
  total_requests: number
  total_tokens: number
  net_paid_units: number
}

export interface UserList {
  users: UserRow[]
  total: number
  limit: number
  offset: number
}

export interface UserQuery {
  q?: string
  exact_id?: boolean
  paid_only?: boolean
  kind?: UserKind
  sort?: UserSort
  order?: 'asc' | 'desc'
  limit?: number
  offset?: number
}

export interface UserWallet {
  account_id: string
  paid_units: number
  bonus_units: number
  balance_units: number
  balance_petals: number
  available_units: number
  held_units: number
  unsettled_units: number
}

export interface UserStats {
  days: number
  total_cost_units: number
  total_requests: number
  total_input_tokens: number
  total_output_tokens: number
  total_cached_tokens: number
  by_model: { model: string; requests: number; cost_units: number; tokens: number }[]
  daily: { date: string; requests: number; cost_units: number; input_tokens: number; output_tokens: number }[]
  recent: {
    request_id: string
    model: string
    status: string
    error_code: string
    duration_ms: number
    input_tokens: number
    output_tokens: number
    cost_units: number
    created_at: string
  }[]
}

export type LedgerFamily = '' | 'money' | 'usage'

export interface LedgerEntry {
  id: string
  kind: string
  family: 'usage' | 'money-in' | 'money-out'
  direction: 'in' | 'out'
  amount_units: number
  paid_units: number
  bonus_units: number
  occurred_at: string
  channel: string | null
  ref: string
  request_id: string | null
  model: string | null
  note: string
  operator: string
  balance_after_units: number | null
}

export interface UserLedger {
  entries: LedgerEntry[]
  total: number
  limit: number
  offset: number
  family: string
  summary: {
    total_in_units: number
    total_out_units: number
    net_units: number
    paid_in_units: number
    money_count: number
    usage_count: number
    balance_units: number
    unrecorded_delta_units: number
    balance_petals: number
  }
}

export interface BanRecord {
  id: number
  ban_type: string
  reason: string
  banned_by: string
  banned_at: string
  lifted_at: string | null
  lifted_by: string | null
  lift_reason: string
}

export interface RechargeBody {
  amount_petals: number
  note: string
  as_bound: boolean
  confirm_large_normal_grant: boolean
  ref: string
}

export interface RechargeResult {
  txn_id: string
  created: boolean
  as_bound: boolean
  balance_units: number
  balance_petals: number
}

export interface BatchItem {
  line: number
  raw: string
  target: string
  amount_petals: number
  note: string
  error: string
  account_id?: string
  label?: string
  amount_units?: number
  balance_before_units?: number
  balance_after_units?: number
  warning?: string
}

export interface BatchSummary {
  total: number
  ok: number
  errors: number
  warnings: number
  user_count: number
  add_petals: number
  sub_petals: number
  net_petals: number
  as_bound: boolean
  large_normal_grants: number
}

export interface BatchResult {
  dry_run: boolean
  executed: boolean
  items: BatchItem[]
  summary: BatchSummary
}

export interface BatchBody {
  text: string
  dry_run: boolean
  as_bound: boolean
  confirm_large_normal_grant: boolean
  ref?: string
}

// ------------------------------------------------------------------ 充值订单

export type PayChannel = 'stripe' | 'admin' | 'activation'
export type PayStatus = 'pending' | 'paid' | 'closed'
export type RefundStatus = 'none' | 'partial' | 'refunded' | 'refunding'

export interface RechargeRow {
  kind: 'order' | 'grant'
  id: string
  order_id: string | null
  account_id: string
  email: string
  display_name: string
  pay_channel: PayChannel
  pay_status: PayStatus
  status: string
  sku: string
  currency: string
  amount_minor: number
  credit_units: number
  bonus_units: number
  channel_txn_id: string
  order_source: string
  refund_status: RefundStatus
  refunded_minor: number
  refund_at: string | null
  refund_reason: string
  created_at: string
  paid_at: string | null
  memo: string
  last_error: string
}

export interface RechargeSummary {
  by_currency: { currency: string; gross_minor: number; refunded_minor: number; net_minor: number; orders: number }[]
  pending: number
  paid: number
  closed: number
  with_refund: number
  total: number
}

export interface RechargeList {
  data: RechargeRow[]
  total: number
  limit: number
  offset: number
  user_lookup_failed: boolean
  summary: RechargeSummary
}

export interface RechargeQuery {
  q?: string
  pay_status?: string
  pay_channel?: string
  order_source?: string
  refund_status?: string
  limit?: number
  offset?: number
}

export interface RefundInfo {
  order_id: string
  refund_status: RefundStatus
  order_status: string
  currency: string
  amount_minor: number
  refunded_minor: number
  refundable_minor: number
  pending_clawback: number
  refunds: {
    id: string
    kind: string
    channel_refund_id: string
    amount_minor: number
    status: string
    reason: string
    paid_units: number
    bonus_units: number
    clawed_at: string | null
    reversed_at: string | null
    created_at: string
  }[]
}

// ------------------------------------------------------------------ 激活码

export interface PoolTier {
  amount_units: number
  count: number
  claimed?: number
}

export interface PoolSummary {
  id: number
  size: number
  claimed_count: number
  total_units: number
  claimed_units: number
  tiers: PoolTier[]
}

export interface CodeRow {
  id: number
  code: string
  batch_id: string | null
  amount_units: number
  amount_max_units: number | null
  max_total_uses: number
  max_uses_per_user: number
  used_count: number
  valid_from: string | null
  expires_at: string
  status: 'active' | 'disabled'
  note: string
  created_by: string
  benefit_type: string
  pool_id: number | null
  created_at: string
  redeemed_units: number
}

export interface CodeDetail extends CodeRow {
  pool?: PoolSummary | null
}

export interface CodeList {
  codes: CodeRow[]
  total: number
  limit: number
  offset: number
}

export interface CodeQuery {
  q?: string
  batch_id?: string
  status?: string
  limit?: number
  offset?: number
}

export interface Redemption {
  account_id: string
  email: string
  display_name: string
  amount_units: number
  balance_after_units: number | null
  created_at: string
}

export interface CodeStats {
  total_codes: number
  total_value_units: number
  redeemed_units: number
  redeemed_count: number
  redeem_users: number
  by_batch: {
    batch_id: string
    note: string
    codes: number
    disabled_codes: number
    total_value_units: number
    redeemed_units: number
    redeemed_count: number
  }[]
}

export interface CodeBlock {
  account_id: string
  email: string
  display_name: string
  fail_count: number
  block_level: number
  blocked_until: string | null
  last_failed_at: string | null
}

export interface CodeCreateBody {
  amount_units?: number | null
  amount_max_units?: number | null
  max_total_uses: number
  max_uses_per_user: number
  valid_from?: string | null
  expires_at: string
  note: string
  prize_pool?: { tiers: { amount_units: number; count: number }[] } | null
}

export interface GenerateResult {
  batch_id: string
  codes: string[]
  pool?: { id: number; size: number; total_units: number; min_units: number; max_units: number }
}

export interface CodePatch {
  status?: 'active' | 'disabled'
  expires_at?: string
  valid_from?: string
  note?: string
  max_total_uses?: number
  max_uses_per_user?: number
}

// ------------------------------------------------------------------ 请求层

function failure(status: number, body: unknown): ApiError {
  const err = (body as { error?: { code?: string; message?: string } } | null)?.error
  return new ApiError(status, err?.code ?? `http_${status}`, err?.message ?? 'Request failed')
}

function url(path: string, query?: Query): string {
  if (!query) return path
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '' || v === false) continue
    qs.set(k, String(v))
  }
  const s = qs.toString()
  return s ? `${path}?${s}` : path
}

async function request<T>(method: string, path: string, query?: Query, body?: unknown): Promise<T> {
  const target = url(path, query)
  for (let attempt = 0; attempt < 2; attempt++) {
    let resp: Response
    try {
      resp = await fetch(target, {
        method,
        credentials: 'same-origin',
        headers: body === undefined ? { Accept: 'application/json' } : { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: body === undefined ? undefined : JSON.stringify(body),
      })
    } catch (e) {
      console.error('[api] 网络错误', method, target, e)
      throw new ApiError(0, 'network_error', 'Network error')
    }
    const text = await resp.text()
    let parsed: unknown
    try {
      parsed = text ? JSON.parse(text) : undefined
    } catch {
      parsed = undefined
    }
    if (resp.status === 401 && attempt === 0) {
      try {
        await adminApi.me()
      } catch (e) {
        console.warn('[api] 会话刷新失败，不再重试', e)
        throw failure(resp.status, parsed)
      }
      continue
    }
    if (!resp.ok) {
      const err = failure(resp.status, parsed)
      console.warn('[api] 请求失败', method, target, err.status, err.code)
      throw err
    }
    return parsed as T
  }
  throw new ApiError(401, 'not_logged_in', 'Login required.')
}

const C = '/control/admin'
const B = '/billing/admin'
const I = '/admin/identity/accounts'

export const usersApi = {
  // 用户与计费
  users: (q: UserQuery) => request<UserList>('GET', `${C}/users`, q as Query),
  wallet: (id: string) => request<UserWallet>('GET', `${C}/users/${id}/balance`),
  keys: (id: string) => request<{ keys: ApiKey[] }>('GET', `${C}/users/${id}/keys`),
  stats: (id: string, days: number) => request<UserStats>('GET', `${C}/users/${id}/stats`, { days }),
  ledger: (id: string, family: LedgerFamily, limit: number, offset: number) =>
    request<UserLedger>('GET', `${C}/users/${id}/balance-ledger`, { family, limit, offset }),
  bans: (id: string) => request<{ account_id: string; bans: BanRecord[] }>('GET', `${I}/${id}/bans`),
  ban: (id: string, reason: string) => request<{ ok: boolean }>('POST', `${I}/${id}/ban`, undefined, { reason }),
  unban: (id: string, reason: string) => request<{ ok: boolean }>('POST', `${I}/${id}/unban`, undefined, { reason }),
  setInternal: (id: string, internal: boolean, reason: string) =>
    request<{ ok: boolean; is_internal: boolean }>('POST', `${I}/${id}/internal`, undefined, { internal, reason }),
  setGeoExempt: (id: string, exempt: boolean, reason: string) =>
    request<{ ok: boolean; geo_exempt: boolean }>('POST', `${I}/${id}/geo-exempt`, undefined, { exempt, reason }),
  recharge: (id: string, body: RechargeBody) => request<RechargeResult>('POST', `${C}/users/${id}/recharge`, undefined, body),
  batchAdjust: (body: BatchBody) => request<BatchResult>('POST', `${C}/users/batch-adjust`, undefined, body),

  // 充值订单
  rechargeOrders: (q: RechargeQuery) => request<RechargeList>('GET', `${B}/recharge-orders`, q as Query),
  refundStatus: (orderId: string) => request<RefundInfo>('GET', `${B}/recharge-orders/${orderId}/refund-status`),
  syncOrder: (orderId: string) => request<unknown>('POST', `${B}/orders/${orderId}/sync`),
  refundOrder: (orderId: string, amount_minor: number, reason: string) =>
    request<unknown>('POST', `${B}/orders/${orderId}/refund`, undefined, { amount_minor, reason }),

  // 激活码
  codeStats: (batch_id = '') => request<CodeStats>('GET', `${C}/activation-codes/stats`, { batch_id }),
  codes: (q: CodeQuery) => request<CodeList>('GET', `${C}/activation-codes`, q as Query),
  code: (id: number) => request<CodeDetail>('GET', `${C}/activation-codes/${id}`),
  redemptions: (id: number, limit: number, offset: number) =>
    request<{ total: number; limit: number; offset: number; redemptions: Redemption[] }>(
      'GET',
      `${C}/activation-codes/${id}/redemptions`,
      { limit, offset },
    ),
  generateCodes: (body: CodeCreateBody & { count: number }) =>
    request<GenerateResult>('POST', `${C}/activation-codes/generate`, undefined, body),
  createCode: (body: CodeCreateBody & { code: string }) =>
    request<{ id: number; code: string }>('POST', `${C}/activation-codes`, undefined, body),
  patchCode: (id: number, body: CodePatch) => request<{ ok: boolean }>('PATCH', `${C}/activation-codes/${id}`, undefined, body),
  deleteCode: (id: number) =>
    request<{ deleted: boolean; code_id: number; redemptions_deleted: number }>('DELETE', `${C}/activation-codes/${id}`),
  disableBatch: (batch: string) =>
    request<{ batch_id: string; disabled_count: number }>('POST', `${C}/activation-codes/batch/${encodeURIComponent(batch)}/disable`),
  enableBatch: (batch: string) =>
    request<{ batch_id: string; enabled_count: number }>('POST', `${C}/activation-codes/batch/${encodeURIComponent(batch)}/enable`),
  noteBatch: (batch: string, note: string) =>
    request<{ batch_id: string; updated_count: number }>('PATCH', `${C}/activation-codes/batch/${encodeURIComponent(batch)}`, undefined, { note }),
  deleteBatch: (batch: string) =>
    request<{ batch_id: string; codes_deleted: number; redemptions_deleted: number }>(
      'DELETE',
      `${C}/activation-codes/batch/${encodeURIComponent(batch)}`,
    ),
  exportUrl: (batch: string) => url(`${C}/activation-codes/export`, { batch_id: batch }),
  blocks: () => request<{ blocks: CodeBlock[] }>('GET', `${C}/activation-blocks`),
  unblock: (accountId: string, reset_level: boolean) =>
    request<{ ok: boolean }>('POST', `${C}/activation-blocks/${accountId}/unblock`, undefined, { reset_level }),
}
